import { useEffect, useState } from "react";

export interface ContributionDay {
    date: string;
    count: number;
    level: 0 | 1 | 2 | 3 | 4;
}

interface OfficialResponse {
    total: number;
    contributions: ContributionDay[];
}

interface FallbackResponse {
    total: { [year: string]: number };
    contributions: ContributionDay[];
}

export const FALLBACK_TOTAL = 847;

/**
 * Last year of GitHub contributions: the site's own API route first, then a
 * public mirror. Shared by the About bento card and the Profile scene so both
 * show the same numbers from one fetch path.
 */
export function useGitHubContributions(username: string) {
    const [data, setData] = useState<ContributionDay[] | null>(null);
    const [total, setTotal] = useState<number>(FALLBACK_TOTAL);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        let cancelled = false;

        async function tryOfficial(): Promise<boolean> {
            try {
                const res = await fetch(`/api/github-contributions?username=${username}`);
                if (!res.ok) return false;
                const json: OfficialResponse = await res.json();
                if (cancelled) return true;
                setData(json.contributions);
                setTotal(json.total ?? FALLBACK_TOTAL);
                return true;
            } catch {
                return false;
            }
        }

        async function tryFallback(): Promise<boolean> {
            try {
                const res = await fetch(
                    `https://github-contributions-api.jogruber.de/v4/${username}?y=last`
                );
                if (!res.ok) return false;
                const json: FallbackResponse = await res.json();
                if (cancelled) return true;
                setData(json.contributions);
                const year = Object.keys(json.total)[0];
                setTotal(json.total[year] ?? FALLBACK_TOTAL);
                return true;
            } catch {
                return false;
            }
        }

        async function load() {
            const ok = (await tryOfficial()) || (await tryFallback());
            if (!cancelled) {
                if (!ok) setError(true);
                setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [username]);

    return { data, total, loading, error };
}

/** Group the last `count` weeks of days into columns of seven. */
export function toWeeks(data: ContributionDay[] | null, count: number): ContributionDay[][] {
    const weeks: ContributionDay[][] = [];
    if (!data) return weeks;
    const last = data.slice(-count * 7);
    for (let w = 0; w < count; w++) weeks.push(last.slice(w * 7, w * 7 + 7));
    return weeks;
}
